/* eslint @typescript-eslint/no-unused-vars:0 */
import {
  Form,
  Loading,
  Logout,
  Menu as RaMenu,
  Title,
  useGetOne,
  useLogout,
  usePermissions,
  useRedirect,
  useTheme,
  useTranslate
} from 'react-admin'
import HomeIcon from '@mui/icons-material/Home'
import SquraeIcon from '@mui/icons-material/Square'
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  Typography,
  useMediaQuery,
  Paper
} from '@mui/material'
import React from 'react'
import { Footer } from 'components/Footer/inex'
import { type Partner, type Product } from '@mzc-watchover/adminapp-appsync'
import { useGetConfig } from 'components/DataProvider'
import {
  find,
  includes,
  intersection,
  isEmpty,
  isNil,
  startsWith
} from 'lodash/fp'
import log from 'loglevel'
import { type ConfigProps } from 'components/Dashboard'
import { Announce } from 'components/Announce'
import { ScrollNone, ScrollTo, useNotify } from 'components/Util'
import { AllData2 } from 'components/AllData2'
import { Accounts } from 'components/Accounts'
import { Mapping } from 'components/Mapping'
import { CognitoLog } from 'components/CognitoLog'
import { CommentLog } from 'components/CommentLog'
import { AdminDashboard } from 'components/AdminDashboard'
import { EditData } from 'components/EditData'
import { DemandAlert } from 'components/DemandAlert'
import { Spot2 } from 'components/Spot2'
import { Dashboard2 } from 'components/Dashboard2'
import { useParams, useSearchParams } from 'react-router-dom'
import { DrawerContext, PartnerContext } from 'components/Admin'

export const Menu: React.FC<{
  permissions: string[]
  setOpen: (open: boolean) => void
}> = ({ setOpen, permissions }) => {
  const translate = useTranslate()
  const { partner } = React.useContext(PartnerContext)
  const { data: configData, isLoading: isConfigLoading } = useGetConfig(
    partner ?? ''
  )
  const [enableDashboard, setEnableDashboard] = React.useState<
    string[] | undefined
  >(undefined)
  const handleSelect = (): void => {
    setOpen(false)
  }
  React.useEffect(() => {
    if ((isConfigLoading as boolean) || isNil(configData)) return
    const { Accounts: AccountsConfig } = configData
    const { dashboard } = AccountsConfig
    if (!isNil(dashboard)) {
      setEnableDashboard(dashboard as string[])
    }
  }, [isConfigLoading, configData])
  const width = 400
  if (isConfigLoading as boolean) return null
  return (
    <Paper sx={{ width }}>
      <RaMenu>
        {!isNil(
          find(
            (item: string) =>
              item === 'owner' ||
              item === 'partner' ||
              item === 'user' ||
              item === 'highManager' ||
              startsWith('manager')(item)
          )(permissions)
        ) && (
          <>
            <RaMenu.Item
              to="/"
              primaryText={translate('menu.data')}
              leftIcon={<SquraeIcon fontSize="small" />}
              onClick={handleSelect}
              sx={{ width }}
            />
            <List sx={{ ml: '1em' }}>
              {!isNil(
                find(
                  (item: string) =>
                    item === 'owner' ||
                    item === 'user' ||
                    item === 'highManager' ||
                    startsWith('manager')(item)
                )(permissions)
              ) &&
                (isNil(enableDashboard) ||
                  !isEmpty(intersection(permissions)(enableDashboard))) && (
                  <RaMenu.Item
                    to="dashboard"
                    primaryText={translate('menu.dataItems.dashboard')}
                    onClick={handleSelect}
                    sx={{ width }}
                  />
                )}
              <RaMenu.Item
                to="spot"
                primaryText={translate('menu.dataItems.spot')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="allData"
                primaryText={translate('menu.dataItems.allData')}
                onClick={handleSelect}
                sx={{ width }}
              />
            </List>
          </>
        )}
        {includes('admin')(permissions) && (
          <>
            <RaMenu.Item
              to="admin"
              primaryText={translate('menu.admin')}
              leftIcon={<SquraeIcon fontSize="small" />}
              onClick={handleSelect}
              sx={{ width }}
            />
            <List sx={{ ml: '1em' }}>
              <RaMenu.Item
                to="admin/accounts/admin"
                primaryText={translate('menu.adminItems.accounts')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin/spots/admin"
                primaryText={translate('menu.adminItems.spots')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin?command=loginLog"
                primaryText={translate('menu.adminItems.loginLog')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin/announces/admin"
                primaryText={translate('menu.adminItems.announces')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin/data/admin"
                primaryText={translate('menu.adminItems.data')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin/demand/admin"
                primaryText={translate('menu.adminItems.demand')}
                onClick={handleSelect}
                sx={{ width }}
              />
              <RaMenu.Item
                to="admin?command=commentLog"
                primaryText={translate('menu.adminItems.commentLog')}
                onClick={handleSelect}
                sx={{ width }}
              />
            </List>
          </>
        )}
        <RaMenu.Item
          to={startsWith('/v2')(window.location.pathname) ? '/v2' : '/'}
          primaryText={translate('menu.home')}
          leftIcon={<HomeIcon />}
          onClick={handleSelect}
          sx={{ width }}
        />
        <Logout />
      </RaMenu>
    </Paper>
  )
}
export const AdminMenuContent: React.FC<{
  partnerName: string
  partner: Partial<Partner>
  config: ConfigProps & { Chart: ConfigProps }
  command: string | null | undefined
  permissions: string[]
}> = ({ partnerName, partner, config, command, permissions }) => {
  const [params, setParams] = useSearchParams()
  const translate = useTranslate()
  const [openCognitoLog, setOpenCognitoLog] = React.useState(
    command === 'loginLog'
  )
  const [openCommentLog, setOpenCommentLog] = React.useState(
    command === 'commentLog'
  )
  const [scroll, setScroll] = React.useState<
    React.RefObject<HTMLDivElement> | undefined
  >(undefined)
  const redirect = useRedirect()
  React.useEffect(() => {
    if (!isNil(command)) {
      if (!isNil(scroll) && !isNil(scroll.current)) {
        scroll.current.scrollIntoView()
      }
    }
    const commandParam = params.get('command')
    setParams('')
    if (commandParam === 'loginLog') setOpenCognitoLog(true)
    if (commandParam === 'commentLog') setOpenCommentLog(true)
  }, [command, params])
  const handleAccounts = (): void => {
    redirect('accounts/admin')
  }
  const handleSpots = (): void => {
    redirect('spots/admin')
  }
  const handleLoginLog = (): void => {
    setOpenCognitoLog(true)
  }
  const handleAnnounces = (): void => {
    redirect('announces/admin')
  }
  const handleData = (): void => {
    redirect('data/admin')
  }
  const handleDemand = (): void => {
    redirect('demand/admin')
  }
  const handleComment = (): void => {
    setOpenCommentLog(true)
  }
  if (!includes('admin')(permissions)) return null
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '95%' }}>
          <Title title={`${partnerName} 管理画面`} />
          <Card
            sx={{
              width: '100%',
              marginTop: '1em',
              marginBottom: '1em',
              backgroundColor: 'background'
            }}>
            <CardContent>
              {command === 'accounts' ? (
                <Grid size={12}>
                  <Typography variant="h5" sx={{ mb: '1em' }}>
                    {translate('menu.adminItems.accounts')}
                  </Typography>
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={() => (
                      <Accounts config={config} partner={partner} />
                    )}
                  />
                </Grid>
              ) : command === 'spots' ? (
                <Grid size={12}>
                  <Typography variant="h5" sx={{ mb: '1em' }}>
                    {translate('menu.adminItems.spots')}
                  </Typography>
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={() => (
                      <Mapping config={config} partner={partner} />
                    )}
                  />
                </Grid>
              ) : command === 'announces' ? (
                <Grid size={12}>
                  <Typography variant="h5" sx={{ mb: '1em' }}>
                    {translate('resources.admin.announces.top')}
                  </Typography>
                  <AdminDashboard partner={partner.id as string} />
                </Grid>
              ) : command === 'data' ? (
                <Grid size={12}>
                  <Typography variant="h5" sx={{ mb: '1em' }}>
                    {translate('resources.editData.title')}
                  </Typography>
                  <EditData config={config} partner={partner} />
                </Grid>
              ) : command === 'demand' ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ width: '60%' }}>
                    <Grid size={12}>
                      <Typography variant="h5" sx={{ mb: '1em' }}>
                        {translate('resources.demandAlert.title')}
                      </Typography>
                      <Form>
                        <DemandAlert config={config} partner={partner} />
                      </Form>
                    </Grid>
                  </Box>
                </Box>
              ) : (
                <Grid
                  container
                  columns={{ xs: 11 }}
                  spacing={2}
                  sx={{ justifyContent: 'center' }}>
                  <Grid size={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAccounts}>
                      {translate('menu.adminItems.accounts')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button variant="contained" fullWidth onClick={handleSpots}>
                      {translate('menu.adminItems.spots')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleLoginLog}>
                      {translate('menu.adminItems.loginLog')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAnnounces}>
                      {translate('menu.adminItems.announces')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button variant="contained" fullWidth onClick={handleData}>
                      {translate('menu.adminItems.data')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleDemand}>
                      {translate('menu.adminItems.demand')}
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleComment}>
                      {translate('menu.adminItems.commentLog')}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
      {openCognitoLog && (
        <Form>
          <CognitoLog
            partner={partner.id as string}
            open={openCognitoLog}
            setOpen={setOpenCognitoLog}
          />
        </Form>
      )}
      {openCommentLog && (
        <Form>
          <CommentLog
            partner={partner.id as string}
            open={openCommentLog}
            setOpen={setOpenCommentLog}
          />
        </Form>
      )}
    </>
  )
}
export const MenuBoardContent: React.FC<{
  partnerName: string
  partner: Partial<Partner>
  config: ConfigProps & { Chart: ConfigProps }
  dashboard: boolean
  spot: boolean
  allData: boolean
  target: string | null | undefined
  kind: string | null | undefined
  date: string | null | undefined
  permissions: string[]
}> = ({
  partnerName,
  partner,
  config,
  dashboard,
  spot,
  allData,
  target,
  kind,
  date,
  permissions
}) => {
  const translate = useTranslate()
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isLarge = useMediaQuery((theme: any) => theme.breakpoints.up('lg'))
  const buttonWidth = isDesktop ? (isLarge ? '230px' : '210px') : '450px'
  const buttonHeight = isDesktop ? '60px' : '40px'
  const redirect = useRedirect()
  const [scroll, setScroll] = React.useState<
    React.RefObject<HTMLDivElement> | undefined
  >(undefined)
  const { setOpen } = React.useContext(DrawerContext)
  const handleDashboard = (): void => {
    setOpen(false)
    redirect('dashboard')
  }
  const handleSpot = (): void => {
    setOpen(false)
    redirect('spot')
  }
  const handleAllData = (): void => {
    setOpen(false)
    redirect('allData')
  }
  const handleAdmin = (): void => {
    setOpen(false)
    redirect('admin')
  }
  React.useEffect(() => {
    if (spot) {
      if (!isNil(scroll) && !isNil(scroll.current)) {
        scroll.current.scrollIntoView()
      }
    }
  }, [spot])
  const { Accounts: AccountsConfig } = config
  const { dashboard: enableDashboard } = AccountsConfig ?? {
    dashboard: undefined
  }
  if (isEmpty(partnerName)) return null
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '95%' }}>
        <Title title={`${partnerName}`} />
        {dashboard ? (
          <Card
            sx={{
              width: '100%',
              backgroundColor: 'background'
            }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <Box sx={{ width: '100%' }}>
                  {!isNil(partner.id) && (
                    <ScrollTo
                      setScroll={setScroll}
                      renderItem={(props) => (
                        <Dashboard2
                          {...props}
                          config={config}
                          partner={partner.id as string}
                          permissions={permissions}
                        />
                      )}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : spot ? (
          <Card
            sx={{
              width: '100%',
              backgroundColor: 'background'
            }}>
            <CardContent>
              {!isNil(partner.id) && (
                <ScrollNone
                  setScroll={setScroll}
                  renderItem={(props) => (
                    <Spot2
                      {...props}
                      partner={partner.id as string}
                      config={config}
                      target={target}
                      date={date}
                      kind={kind}
                      permissions={permissions}
                    />
                  )}
                />
              )}
            </CardContent>
          </Card>
        ) : allData ? (
          <Card
            sx={{
              width: '100%',
              backgroundColor: 'background'
            }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                <Box sx={{ width: '80%' }}>
                  {!isNil(partner.id) && (
                    <AllData2
                      config={config}
                      partner={partner.id}
                      permissions={permissions}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            <Announce partner={partner.id as string} />
            <Card raised sx={{ width: '100%', marginTop: '1em' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      flexDirection: window.innerWidth <= 900 ? 'column' : 'row'
                    }}>
                    {!isNil(
                      find(
                        (item: string) =>
                          item === 'owner' ||
                          item === 'user' ||
                          item === 'highManager' ||
                          startsWith('manager')(item)
                      )(permissions)
                    ) && (
                      <Box
                        sx={{
                          width: window.innerWidth <= 900 ? '95%' : '70%'
                        }}>
                        <Typography
                          variant="h5"
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: '0.5em'
                          }}>
                          {translate('menu.data')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            gap: 2,
                            mt: '1em',
                            mb: '1em'
                          }}>
                          {(isNil(enableDashboard) ||
                            !isEmpty(
                              intersection(permissions)(enableDashboard)
                            )) && (
                            <Button
                              id="spot"
                              variant="contained"
                              color="primary"
                              size="large"
                              sx={{
                                width: buttonWidth,
                                height: buttonHeight,
                                borderRadius: '10px'
                              }}
                              onClick={handleDashboard}>
                              <Typography variant="h6">
                                {translate('menu.dataItems.dashboard')}
                              </Typography>
                            </Button>
                          )}
                          <Button
                            id="spot"
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              width: buttonWidth,
                              height: buttonHeight,
                              borderRadius: '10px'
                            }}
                            onClick={handleSpot}>
                            <Typography variant="h6">
                              {translate('menu.dataItems.spot')}
                            </Typography>
                          </Button>
                          <Button
                            id="spot"
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              width: buttonWidth,
                              height: buttonHeight,
                              borderRadius: '10px'
                            }}
                            onClick={handleAllData}>
                            <Typography variant="h6">
                              {translate('menu.dataItems.allData')}
                            </Typography>
                          </Button>
                        </Box>
                      </Box>
                    )}
                    {!isNil(
                      find(
                        (item: string) =>
                          item === 'user' ||
                          item === 'highManager' ||
                          startsWith('manager')(item)
                      )(permissions)
                    ) &&
                      includes('admin')(permissions) && (
                        <Divider
                          orientation="vertical"
                          variant="middle"
                          flexItem
                        />
                      )}
                    {includes('admin')(permissions) && (
                      <Box>
                        <Typography
                          variant="h5"
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mt: '0.5em'
                          }}>
                          {translate('menu.admin')}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            mt: '1em',
                            mb: '1em'
                          }}>
                          <Button
                            id="spot"
                            variant="contained"
                            color="primary"
                            size="large"
                            sx={{
                              width: buttonWidth,
                              height: buttonHeight,
                              borderRadius: '10px'
                            }}
                            onClick={handleAdmin}>
                            <Typography variant="h6">
                              {translate('menu.adminItems.board')}
                            </Typography>
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Card>
            {!isNil(
              find(
                (item: string) =>
                  item === 'owner' ||
                  item === 'user' ||
                  item === 'highManager' ||
                  startsWith('manager')(item)
              )(permissions)
            ) &&
              (isNil(enableDashboard) ||
                !isEmpty(intersection(permissions)(enableDashboard))) && (
                <Card raised sx={{ width: '100%', marginTop: '1em' }}>
                  <ScrollTo
                    setScroll={setScroll}
                    renderItem={(props) => (
                      <Dashboard2
                        {...props}
                        partner={partner.id as string}
                        config={config}
                        permissions={permissions}
                      />
                    )}
                  />
                </Card>
              )}
          </>
        )}
      </Box>
    </Box>
  )
}
export const MenuBoard: React.FC<{
  partner: string
  dashboard?: boolean
  spot?: boolean
  allData?: boolean
  admin?: string
}> = ({ partner, dashboard, spot, allData, admin }) => {
  const { target, kind, date } = useParams()
  const { permissions, isLoading: isLoadingPemissions } = usePermissions()
  const [_products, setProducts] = React.useState<
    Array<Partial<Product>> | undefined
  >(undefined)
  const { data: configData, isLoading: isConfigLoading } = useGetConfig(partner)
  const [partnerName, setPartnerName] = React.useState<string | undefined>(
    undefined
  )
  const [_theme, setTheme] = useTheme()
  const notify = useNotify()
  const logout = useLogout()
  const { data, isLoading } = useGetOne<{ id: string } & Partial<Partner>>(
    'partners',
    { id: partner }
  )
  React.useEffect(() => {
    if (isLoading) return
    setTheme('light')
    if (!isNil(data)) {
      const { name, products } = data
      setPartnerName(`${name as string}さま`)
      setProducts(products)
    }
  }, [isLoading])
  if (isLoading || isLoadingPemissions || (isConfigLoading as boolean))
    return <Loading />
  if (isNil(data) || isNil(configData)) {
    notify('resources.dashboard.notification.cannotUse', { type: 'error' })
    logout()
      .then(() => {})
      .catch(log.error)
    return <></>
  }
  return (
    <>
      {!isNil(admin) ? (
        <AdminMenuContent
          partnerName={partnerName ?? ''}
          partner={data}
          config={configData as any}
          command={admin}
          permissions={permissions}
        />
      ) : (
        <MenuBoardContent
          partnerName={partnerName ?? ''}
          partner={data}
          config={configData as any}
          dashboard={dashboard ?? false}
          spot={spot ?? false}
          allData={allData ?? false}
          target={target}
          kind={kind}
          date={date}
          permissions={permissions}
        />
      )}
      <Footer />
    </>
  )
}
